import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from './icons';
import axios from 'axios';
import { useI18n, tx } from './i18n';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import { drawAntenna } from './antennaViz';

const API = process.env.REACT_APP_API_URL || '';

// Kalkulator anten -- natywna zakladka HamLoggera. Silnik obliczeniowy jest
// po stronie backendu (antcalc/, formuly ARRL/Balanis); tu jest UI z czterema
// pod-zakladkami: wyniki, mapa terenu, wizualizacja SVG, kalkulator kabla.

const gardenTypes = (t) => [
  ['garden', t('ant.gardenGarden')],
  ['rooftop', t('ant.gardenRooftop')],
  ['balcony', t('ant.gardenBalcony')],
  ['attic', t('ant.gardenAttic')],
];

function ScoreBadge({ score, label }) {
  const color = score >= 85 ? '#3ecf8e' : score >= 70 ? '#4f8ef7'
    : score >= 55 ? '#f5a623' : score >= 40 ? '#e0a05c' : '#e05c5c';
  return (
    <span style={{
      background: color, color: '#fff', fontWeight: 700, fontSize: 12,
      padding: '3px 10px', borderRadius: 12, whiteSpace: 'nowrap',
    }}>{score} · {label}</span>
  );
}

export default function AntennaCalculatorView({ notify, userProfile, theme }) {
  const { t } = useI18n();
  const [subtab, setSubtab] = useState('calc'); // calc | map | viz | coax
  const [meta, setMeta] = useState(null); // {bands, coax_types}

  // Formularz anteny
  const [selectedBands, setSelectedBands] = useState(['20m']);
  const [form, setForm] = useState({
    max_length_m: 20, max_height_m: 10, has_trees: false,
    garden_type: 'garden', supports: 2, can_bury: false,
    stealth: false, wire_diameter_mm: 2.0, power_w: 100,
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedViz, setSelectedViz] = useState(null); // {viz_type, viz_params, name}

  // Ladowanie metadanych (pasma, kable) raz przy montowaniu
  useEffect(() => {
    axios.get(`${API}/api/antenna/meta`).then(r => setMeta(r.data)).catch(() => {
      notify(t('ant.errLoadCalc'), 'error');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notify]);

  const toggleBand = (bk) => {
    setSelectedBands(prev => prev.includes(bk) ? prev.filter(b => b !== bk) : [...prev, bk]);
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const calculate = useCallback(async () => {
    if (selectedBands.length === 0) { notify(t('ant.selectOneBand'), 'error'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/antenna/calculate`, {
        ...form, bands: selectedBands,
        max_length_m: Number(form.max_length_m), max_height_m: Number(form.max_height_m),
        // Puste pole = domyslna wartosc backendu (Number('') dalby 0 punktow zawieszenia)
        supports: form.supports === '' ? null : Number(form.supports),
        wire_diameter_mm: Number(form.wire_diameter_mm),
        power_w: Number(form.power_w),
      });
      setResults(r.data);
      // Auto-wybierz najlepszego kandydata pierwszego pasma do wizualizacji
      if (r.data[0]?.candidates[0]) {
        const c = r.data[0].candidates[0];
        setSelectedViz({ viz_type: c.viz_type, viz_params: c.viz_params, name: c.name });
      }
    } catch (err) {
      notify(err.response?.data?.error || t('ant.errCalc'), 'error');
    }
    setLoading(false);
  }, [form, selectedBands, notify, t]);

  return (
    <section className="section antenna-calc">
      <div className="antenna-calc__layout">
        {/* ── Panel boczny: formularz ── */}
        <aside className="antenna-calc__sidebar">
          <h3 className="antenna-calc__sec-title">{t('ant.plotConditions')}</h3>

          <label className="antenna-calc__lbl">{t('ant.bands')}</label>
          <div className="antenna-calc__bands">
            {meta && meta.bands.map(({ key: bk, label }) => (
              <button key={bk} type="button"
                className={'antenna-calc__band' + (selectedBands.includes(bk) ? ' antenna-calc__band--on' : '')}
                onClick={() => toggleBand(bk)}>{bk}</button>
            ))}
          </div>

          <label className="antenna-calc__lbl">{t('ant.maxHorizontal')}</label>
          <input type="number" className="input" value={form.max_length_m}
            onChange={e => setField('max_length_m', e.target.value)} min="0" step="0.5" />

          <label className="antenna-calc__lbl">{t('ant.maxHeight')}</label>
          <input type="number" className="input" value={form.max_height_m}
            onChange={e => setField('max_height_m', e.target.value)} min="0" step="0.5" />

          <label className="antenna-calc__lbl">{t('ant.spaceType')}</label>
          <select className="input" value={form.garden_type}
            onChange={e => setField('garden_type', e.target.value)}>
            {gardenTypes(t).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>

          <label className="antenna-calc__lbl">{t('ant.supports')}</label>
          <input type="number" className="input" value={form.supports}
            onChange={e => setField('supports', e.target.value)} min="0" max="10" />

          <label className="antenna-calc__lbl">{t('ant.wireDiameter')}</label>
          <input type="number" className="input" value={form.wire_diameter_mm}
            onChange={e => setField('wire_diameter_mm', e.target.value)} min="0.5" step="0.1" />

          <label className="antenna-calc__lbl">{t('ant.power')}</label>
          <input type="number" className="input" value={form.power_w}
            onChange={e => setField('power_w', e.target.value)} min="1" step="10" />

          <div className="antenna-calc__toggles">
            <label className="antenna-calc__toggle">
              <input type="checkbox" checked={form.has_trees}
                onChange={e => setField('has_trees', e.target.checked)} />
              <span>{t('ant.hasTrees')}</span>
            </label>
            <label className="antenna-calc__toggle">
              <input type="checkbox" checked={form.can_bury}
                onChange={e => setField('can_bury', e.target.checked)} />
              <span>{t('ant.canBuryRadials')}</span>
            </label>
            <label className="antenna-calc__toggle">
              <input type="checkbox" checked={form.stealth}
                onChange={e => setField('stealth', e.target.checked)} />
              <span>{t('ant.stealthReq')}</span>
            </label>
          </div>

          <button className="btn btn--primary" style={{ width: '100%', marginTop: 14 }}
            onClick={calculate} disabled={loading}>
            {loading ? t('ant.calculating') : <><Icon name="chart" size={16} /> {t('ant.calcAntennas')}</>}
          </button>
        </aside>

        {/* ── Główny obszar: pod-zakładki ── */}
        <div className="antenna-calc__content">
          <div className="antenna-calc__tabs">
            <button className={'antenna-calc__tab' + (subtab === 'calc' ? ' antenna-calc__tab--active' : '')}
              onClick={() => setSubtab('calc')}><Icon name="chart" size={16} /> {t('ant.tabResults')}</button>
            <button className={'antenna-calc__tab' + (subtab === 'map' ? ' antenna-calc__tab--active' : '')}
              onClick={() => setSubtab('map')}><Icon name="map" size={16} /> {t('ant.tabTerrainMap')}</button>
            <button className={'antenna-calc__tab' + (subtab === 'viz' ? ' antenna-calc__tab--active' : '')}
              onClick={() => setSubtab('viz')}><Icon name="ruler" size={16} /> {t('ant.tabViz')}</button>
            <button className={'antenna-calc__tab' + (subtab === 'coax' ? ' antenna-calc__tab--active' : '')}
              onClick={() => setSubtab('coax')}><Icon name="plug" size={16} /> {t('ant.tabCoax')}</button>
          </div>

          <div className="antenna-calc__pane">
            {subtab === 'calc' && (
              <ResultsPane results={results} onVisualize={(c) => {
                setSelectedViz({ viz_type: c.viz_type, viz_params: c.viz_params, name: c.name });
                setSubtab('viz');
              }} />
            )}
            {subtab === 'map' && <MapPane notify={notify} userProfile={userProfile} />}
            {subtab === 'viz' && <VizPane selected={selectedViz} />}
            {subtab === 'coax' && <CoaxPane meta={meta} notify={notify} />}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Pod-zakładka: WYNIKI ─────────────────────────────────────────────────────
function ResultsPane({ results, onVisualize }) {
  const { t } = useI18n();
  if (!results) {
    return (
      <div className="antenna-calc__placeholder">
        <div style={{ fontSize: 48, opacity: 0.3 }}>📡</div>
        <p>{t('ant.setConditions')}</p>
        <p style={{ fontSize: 12, opacity: 0.7 }}>
          {t('ant.engineDesc')}
        </p>
      </div>
    );
  }
  if (results.length === 0) {
    return <div className="antenna-calc__placeholder"><p>{t('ant.noResults')}</p></div>;
  }
  return (
    <div className="antenna-calc__results">
      {results.map(band => (
        <div key={band.band_key} className="antenna-calc__band-group">
          <h3 className="antenna-calc__band-title">{band.band}</h3>
          <div className="antenna-calc__cards">
            {band.candidates.map((c, i) => (
              <div key={i} className="antenna-calc__card">
                <div className="antenna-calc__card-head">
                  <span className="antenna-calc__card-name">{c.name}</span>
                  <ScoreBadge score={c.score} label={c.score_label} />
                </div>
                <div className="antenna-calc__dims">
                  {Object.entries(c.dims).map(([k, v]) => (
                    <div key={k} className="antenna-calc__dim">
                      <span className="antenna-calc__dim-k">{k}</span>
                      <span className="antenna-calc__dim-v">{v}</span>
                    </div>
                  ))}
                </div>
                {c.pros.length > 0 && (
                  <ul className="antenna-calc__pros">
                    {c.pros.map((p, j) => <li key={j}>✓ {p}</li>)}
                  </ul>
                )}
                {c.cons.length > 0 && (
                  <ul className="antenna-calc__cons">
                    {c.cons.map((p, j) => <li key={j}>△ {p}</li>)}
                  </ul>
                )}
                <div className="antenna-calc__feed"><strong>{t('ant.feed')}</strong> {c.feed}</div>
                <div className="antenna-calc__tip">💡 {c.tip}</div>
                {c.viz_type && (
                  <button className="btn btn--ghost btn--sm" style={{ marginTop: 8 }}
                    onClick={() => onVisualize(c)}><Icon name="ruler" size={16} /> {t('ant.showViz')}</button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Pod-zakładka: WIZUALIZACJA ───────────────────────────────────────────────
function VizPane({ selected }) {
  const { t } = useI18n();
  const containerRef = useRef(null);
  useEffect(() => {
    if (selected && containerRef.current) {
      const svg = drawAntenna(selected.viz_type, selected.viz_params);
      containerRef.current.innerHTML = svg || `<p style="opacity:.5">${t('ant.noVizForType')}</p>`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);
  if (!selected) {
    return (
      <div className="antenna-calc__placeholder">
        <div style={{ fontSize: 48, opacity: 0.3 }}>📐</div>
        <p>{t('ant.selectAntennaViz')}</p>
      </div>
    );
  }
  return (
    <div className="antenna-calc__viz">
      <h3 className="antenna-calc__band-title">{selected.name}</h3>
      <div ref={containerRef} className="antenna-calc__viz-svg" />
    </div>
  );
}

// ── Pod-zakładka: KABEL / FIDER ──────────────────────────────────────────────
function CoaxPane({ meta, notify }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    coax_type: 'RG-213 / RG-8', length_m: 20, freq_mhz: 14.175,
    antenna_swr: 1.5, power_w: 100,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const calc = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/antenna/coax`, {
        ...form, length_m: Number(form.length_m), freq_mhz: Number(form.freq_mhz),
        antenna_swr: Number(form.antenna_swr), power_w: Number(form.power_w),
      });
      setResult(r.data);
    } catch (err) {
      notify(err.response?.data?.error || t('ant.errCoaxCalc'), 'error');
    }
    setLoading(false);
  }, [form, notify, t]);

  return (
    <div className="antenna-calc__coax">
      <div className="antenna-calc__coax-form">
        <div>
          <label className="antenna-calc__lbl">{t('ant.cableType')}</label>
          <select className="input" value={form.coax_type} onChange={e => setField('coax_type', e.target.value)}>
            {meta && meta.coax_types.map(ct => <option key={ct} value={ct}>{ct}</option>)}
          </select>
        </div>
        <div>
          <label className="antenna-calc__lbl">{t('ant.lengthM')}</label>
          <input type="number" className="input" value={form.length_m}
            onChange={e => setField('length_m', e.target.value)} min="0" step="1" />
        </div>
        <div>
          <label className="antenna-calc__lbl">{t('ant.freqMhz')}</label>
          <input type="number" className="input" value={form.freq_mhz}
            onChange={e => setField('freq_mhz', e.target.value)} min="0.1" step="0.1" />
        </div>
        <div>
          <label className="antenna-calc__lbl">{t('ant.swrAtAntenna')}</label>
          <input type="number" className="input" value={form.antenna_swr}
            onChange={e => setField('antenna_swr', e.target.value)} min="1" step="0.1" />
        </div>
        <div>
          <label className="antenna-calc__lbl">{t('ant.power')}</label>
          <input type="number" className="input" value={form.power_w}
            onChange={e => setField('power_w', e.target.value)} min="1" step="10" />
        </div>
        <button className="btn btn--primary" onClick={calc} disabled={loading}
          style={{ alignSelf: 'end' }}>{loading ?'…': t('ant.calculateBtn')}</button>
      </div>

      {result && (
        <div className="antenna-calc__coax-result">
          <div className="antenna-calc__coax-stats">
            <Stat label={t('ant.matchedLoss')} value={`${result.mll_db} dB`} />
            <Stat label={t('ant.totalLoss')} value={`${result.total_loss_db} dB`}
              hint={t('ant.hintStandingWave')} />
            <Stat label={t('ant.powerAtAntenna')} value={`${result.power_ant_w} W`}
              color={result.efficiency_pct > 80 ? '#3ecf8e' : result.efficiency_pct > 60 ? '#f5a623' : '#e05c5c'} />
            <Stat label={t('ant.efficiency')} value={`${result.efficiency_pct} %`} />
            <Stat label={t('ant.powerLoss')} value={`${result.power_loss_w} W`} />
            <Stat label={t('ant.swrAtTx')} value={result.swr_tx}
              hint={t('ant.hintLowerSwr')} />
            <Stat label={t('ant.electricalLength')} value={`${result.el_wl} λ`} />
            <Stat label={t('ant.peakVoltage')} value={`${result.v_peak_max_v} V`} />
          </div>
          <div className="antenna-calc__coax-note">💡 {result.note}</div>
          <div className="antenna-calc__coax-rec"><strong>{t('ant.recommendation')}</strong> {result.recommendation}</div>

          <h4 className="antenna-calc__band-title" style={{ marginTop: 16 }}>
            {t('ant.cableComparison')} ({result.z0_ohm} Ω, {result.freq_mhz} {tx('MHz,')} {result.length_m} {tx('m)')}
          </h4>
          <table className="antenna-calc__table">
            <thead>
              <tr><th>{t('ant.cable')}</th><th>{t('ant.lossDb')}</th><th>{t('ant.powerAtAntennaW')}</th></tr>
            </thead>
            <tbody>
              {result.comparison.map(c => (
                <tr key={c.name} className={c.selected ? 'antenna-calc__row--selected' : ''}>
                  <td>{c.name}</td>
                  <td>{c.total_db}</td>
                  <td>{c.p_ant_w}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, hint, color }) {
  return (
    <div className="antenna-calc__stat">
      <div className="antenna-calc__stat-label">{label}</div>
      <div className="antenna-calc__stat-value" style={color ? { color } : undefined}>{value}</div>
      {hint && <div className="antenna-calc__stat-hint">{hint}</div>}
    </div>
  );
}

// ── Pod-zakładka: MAPA TERENU ────────────────────────────────────────────────
// Klik na mapie -> pobiera wysokość terenu (n.p.m.) w tym punkcie. Pomaga
// ocenić ekspozycję działki (wzniesienie = lepszy horyzont radiowy). Adres
// można też wyszukać (geokodowanie). Używa react-leaflet jak reszta apki.
function MapPane({ notify, userProfile }) {
  const { t } = useI18n();
  const home = [userProfile?.home_lat || 52.0, userProfile?.home_lon || 19.0];
  const [marker, setMarker] = useState(null); // {lat, lon}
  const [elevation, setElevation] = useState(null);
  const [loadingElev, setLoadingElev] = useState(false);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [mapCenter, setMapCenter] = useState(home);

  const fetchElevation = useCallback(async (lat, lon) => {
    setLoadingElev(true);
    setElevation(null);
    try {
      const r = await axios.get(`${API}/api/antenna/elevation`, { params: { lat, lon } });
      setElevation(r.data.elevation_m);
    } catch {
      setElevation(null);
    }
    setLoadingElev(false);
  }, []);

  const onPick = useCallback((lat, lon) => {
    setMarker({ lat, lon });
    fetchElevation(lat, lon);
  }, [fetchElevation]);

  const doGeocode = async () => {
    if (!search.trim()) return;
    try {
      const r = await axios.get(`${API}/api/antenna/geocode`, { params: { q: search } });
      setSearchResults(r.data);
      if (r.data.length === 0) notify(t('ant.errLocationNotFound'), 'error');
    } catch { notify(t('ant.errSearch'), 'error'); }
  };

  const pickResult = (res) => {
    setMapCenter([res.lat, res.lon]);
    setSearchResults([]);
    setSearch(res.display);
    onPick(res.lat, res.lon);
  };

  return (
    <div className="antenna-calc__map">
      <div className="antenna-calc__map-toolbar">
        <input className="input" placeholder={t('ant.searchAddress')}
          value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && doGeocode()} />
        <button className="btn btn--secondary btn--sm" onClick={doGeocode}><Icon name="search" size={16} /> {t('ant.searchBtn')}</button>
      </div>
      {searchResults.length > 0 && (
        <div className="antenna-calc__geo-results">
          {searchResults.map((r, i) => (
            <div key={i} className="antenna-calc__geo-item" onClick={() => pickResult(r)}>
              📍 {r.display}
            </div>
          ))}
        </div>
      )}

      <div className="antenna-calc__map-info">
        {marker ? (
          <>
            <span>📍 {marker.lat.toFixed(5)}, {marker.lon.toFixed(5)}</span>
            <span>⛰️ {t('ant.elevation')} {loadingElev ?'…': (elevation != null ? `${elevation} ${t('ant.aboveSeaLevel')}` : t('ant.noDataShort'))}</span>
          </>
        ) : (
          <span style={{ opacity: 0.6 }}>{t('ant.clickMapTerrain')}</span>
        )}
      </div>

      <div className="antenna-calc__map-container">
        <MapContainer center={mapCenter} zoom={11} style={{ height: '100%', width: '100%', background: '#0a1520' }}>
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <MapCenterController center={mapCenter} />
          <MapClickHandler onPick={onPick} />
          {marker && <Marker position={[marker.lat, marker.lon]} />}
        </MapContainer>
      </div>
    </div>
  );
}

function MapClickHandler({ onPick }) {
  useMapEvents({
    click: (e) => onPick(e.latlng.lat, e.latlng.lng),
  });
  return null;
}

function MapCenterController({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
}
